/**
 * Pure decision logic for the Robinhood Universal Router's on-chain Permit2
 * allowance mode (M4 audit finding 1, Option A).
 *
 * M3's Universal Router pulls ERC-20 input through Permit2's ON-CHAIN
 * allowance (`Permit2.allowance(owner, token, spender)`), not a signature.
 * An ERC-20 input trade therefore requires two on-chain approvals before
 * `/v1/build` can simulate successfully:
 *
 *   1. `ERC20.approve(Permit2, amount)` — if the token hasn't already
 *      granted Permit2 a sufficient allowance.
 *   2. `Permit2.approve(token, UniversalRouter, amount, expiration)` — if
 *      the on-chain Permit2 allowance for the Universal Router is missing,
 *      insufficient, or expired.
 *
 * This module contains no React/ethers/wagmi dependencies so it can be
 * unit-tested directly. `useRobinhoodRouterAllowance` (the hook) is a thin
 * wrapper that feeds live on-chain reads into `robinhoodAllowanceState` and
 * turns the decision into the same `Allowance` shape `usePermit2Allowance`
 * returns, so `SwapForm`/`ConfirmSwapModal`/`useConfirmModalState` don't
 * need to know which allowance mode a trade is using.
 */

// IAllowanceTransfer.approve's `amount` and `expiration` parameters are
// uint160 / uint48 respectively; values must be clamped to fit on-chain.
export const MAX_PERMIT2_AMOUNT = (1n << 160n) - 1n
export const MAX_PERMIT2_EXPIRATION = (1n << 48n) - 1n

export function clampToUint160(amount: bigint): bigint {
  if (amount < 0n) {
    return 0n
  }
  return amount > MAX_PERMIT2_AMOUNT ? MAX_PERMIT2_AMOUNT : amount
}

export function clampToUint48(expiration: number): number {
  if (expiration < 0) {
    return 0
  }
  return expiration > MAX_PERMIT2_EXPIRATION ? Number(MAX_PERMIT2_EXPIRATION) : expiration
}

export interface DirectAllowanceReads {
  /** The {chainId, account, token, spender} identity these values were read for. */
  identityKey?: string
  erc20?: bigint
  permit2Amount?: bigint
  permit2Expiration?: number
}

/**
 * Selects the allowance inputs to feed `robinhoodAllowanceState`, gating the direct on-chain reads by
 * identity (audit #8 follow-up): the direct reads are consumed ONLY when their stamped `identityKey`
 * matches the identity being rendered right now, so a pending account/token/spender switch can never
 * momentarily consume the PREVIOUS identity's (possibly ALLOWED) values. When they don't match, it
 * falls back to the multicall reads — which are `undefined` on chains without Uniswap's multicall lens
 * (e.g. 4663), correctly yielding LOADING rather than a stale ALLOWED.
 */
export function selectAllowanceReads(params: {
  identityKey: string
  directReads: DirectAllowanceReads
  multicallErc20?: bigint
  multicallPermit2?: bigint
  multicallPermit2Expiration?: number
}): { erc20ToPermit2?: bigint; permit2ToUR?: bigint; permit2Expiration?: number } {
  const fresh = params.directReads.identityKey === params.identityKey ? params.directReads : undefined
  return {
    erc20ToPermit2: fresh?.erc20 ?? params.multicallErc20,
    permit2ToUR: fresh?.permit2Amount ?? params.multicallPermit2,
    permit2Expiration: fresh?.permit2Expiration ?? params.multicallPermit2Expiration,
  }
}

interface RobinhoodAllowanceInputs {
  /** Current on-chain `ERC20.allowance(owner, Permit2)`. `undefined` = not yet loaded. */
  erc20ToPermit2?: bigint
  /** Current on-chain `Permit2.allowance(owner, token, UniversalRouter).amount`. `undefined` = not yet loaded. */
  permit2ToUR?: bigint
  /** Current on-chain `Permit2.allowance(owner, token, UniversalRouter).expiration`, in unix seconds. `undefined` = not yet loaded. */
  permit2Expiration?: number
  /** The amount that must be spendable by the Universal Router. */
  amount: bigint
  /** Current time in unix seconds. */
  now: number
}

type RobinhoodAllowanceDecision =
  | { kind: 'LOADING' }
  | { kind: 'ALLOWED' }
  | {
      kind: 'REQUIRED'
      /** Step 1: `ERC20.approve(Permit2, amount)` is needed. */
      needsSetupApproval: boolean
      /** Step 2: on-chain `Permit2.approve(token, UniversalRouter, amount, expiration)` is needed. */
      needsPermit2Approval: boolean
    }

/**
 * Decides which (if any) of the two on-chain approval steps are required.
 * A Permit2 EIP-712 signature is never sufficient in this mode — this
 * function has no signature input at all, by design (see audit finding 1).
 */
export function robinhoodAllowanceState({
  erc20ToPermit2,
  permit2ToUR,
  permit2Expiration,
  amount,
  now,
}: RobinhoodAllowanceInputs): RobinhoodAllowanceDecision {
  if (erc20ToPermit2 === undefined || permit2ToUR === undefined || permit2Expiration === undefined) {
    return { kind: 'LOADING' }
  }

  const needsSetupApproval = erc20ToPermit2 < amount
  const permit2Valid = permit2ToUR >= amount && permit2Expiration > now
  const needsPermit2Approval = !permit2Valid

  if (!needsSetupApproval && !needsPermit2Approval) {
    return { kind: 'ALLOWED' }
  }

  return { kind: 'REQUIRED', needsSetupApproval, needsPermit2Approval }
}
